<script setup lang="ts">
import { twMerge } from "tailwind-merge";
import { computed } from "vue";
import { RouterView } from "vue-router";

import { useUser } from "@/composables/api/useUser";
import { useI18n } from "@/i18n";

import NavigationMenu from "@/components/sections/NavigationMenu.vue";
import RewardsSummary from "@/components/sections/RewardsSummary.vue";
import SubmitReceipt from "@/components/sections/submit-receipt/SubmitReceipt.vue";
import HeaderBanner from "@/components/ui/HeaderBanner.vue";
import LactelHeaderBanner from "@/lactel/LactelHeaderBanner.vue";

const { data: user } = useUser();
const { t } = useI18n();

const boostMessage = computed(() => {
  const latestBoost = user.value?.latest_loyalty_boost;

  if (!latestBoost?.type) {
    return null;
  }

  if (latestBoost.type === "register") {
    return t("home.boost.register", { n: latestBoost.loyalty_points });
  }

  if (latestBoost.type === "relog") {
    return t("home.boost.relog", { n: latestBoost.loyalty_points });
  }

  return null;
});
</script>

<template>
  <HeaderBanner v-if="user?.current_mechanic !== 'lactel'" />

  <LactelHeaderBanner v-else />

  <main class="relative">
    <RewardsSummary />

    <div
      v-if="boostMessage"
      class="mb-[25px] flex h-[25px] w-full shrink-0 items-center justify-center px-6"
    >
      <p
        :class="
          twMerge(
            'font-montserrat text-galbani w-full max-w-[342px] text-center text-[18px] leading-normal font-[500] lg:max-w-[640px] lg:text-[20px] [&>strong]:font-[700]',
            user?.current_mechanic === 'lactel' && 'text-lactel-blue',
          )
        "
        v-html="boostMessage"
      />
    </div>

    <NavigationMenu />

    <RouterView v-slot="{ Component }">
      <Transition name="fade" mode="out-in">
        <Component :is="Component" />
      </Transition>
    </RouterView>

    <div
      :class="
        twMerge(
          'fixed right-0 bottom-0 left-0 z-[2] flex w-full items-center justify-center bg-black py-3 lg:hidden',
          user?.current_mechanic === 'lactel' && 'bg-lactel-blue border-lactel-blue-400 border-t-1',
        )
      "
    >
      <SubmitReceipt />
    </div>
  </main>
</template>

<style lang="css" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
